(ns ui.subscription.exit-modal
  "Modal questionnaire shown when a user navigates away from the subscription
   page without subscribing. Collects the reason and optional comments, then
   dispatches the exit-reason command.

   The reasons are shuffled once per opening (see `shuffled-reason-ids` and
   the `:on-load`), with \"Other\" always last, so that the first answer does
   not collect the clicks of everybody who just wants the modal gone. The
   position of the chosen answer travels with the command, which lets the
   admin log show whether the order still shapes the answers.")

(def reasons
  [["too-expensive" "Too expensive"]
   ["just-exploring" "Just exploring, not ready yet"]
   ["need-to-ask-team" "Need to check with my team first"]
   ["missing-features" "Missing features I need"]
   ["pricing-unclear" "Pricing model is unclear"]
   ["prefer-competitor" "Already using a competitor"]
   ["too-difficult" "Looks too difficult to use"]
   ["free-trial" "I'd like to try it for free first"]
   ["other" "Other"]])

(defn shuffled-reason-ids
  []
  (conj (vec (shuffle (map first (butlast reasons))))
        "other"))

(defn ordered-reasons
  "The reasons in the order of this opening, falling back to the definition
   order when the modal was shown without its `:on-load`."
  [state]
  (let [labels (into {} reasons)]
    (map (fn [id]
           [id (labels id)])
         (or (::order state)
             (map first reasons)))))

(defn reason-position
  "1-based position of the selected reason in the shown order."
  [state]
  (let [selected (::selected-reason state)]
    (some (fn [[index [id _]]]
            (when (= id selected)
              (inc index)))
          (map-indexed vector
                       (ordered-reasons state)))))

(defn render
  [state]
  (let [selected-reason (::selected-reason state)
        comment-text (::comment-text state)]
    [:div.py-4.flex.flex-col.items-center.max-w-md.mx-auto
     [:div.w-16.h-16.rounded-full.bg-blue-100.flex.items-center.justify-center.mb-6
      [:i.fas.fa-comment-dots.text-2xl.text-blue-600]]
     [:h3.text-lg.font-semibold.text-gray-900.text-center.mb-2
      "What's holding you back?"]
     [:p.text-sm.text-gray-500.text-center.mb-6
      "We'd love to understand what would make Storrito more valuable for you."]
     [:div.space-y-3.mb-6.w-full
      (map
        (fn [[value label]]
          [:label.flex.items-center.gap-3.cursor-pointer.p-2.rounded.hover:bg-gray-50
           {:key value}
           [:input {:type "radio"
                    :name "exit-reason"
                    :class ["accent-blue-600"]
                    :checked (= selected-reason value)
                    :on {:click [[:store/assoc-in [::selected-reason] value]]}}]
           [:span.text-sm.text-gray-700 label]])
        (ordered-reasons state))]
     [:div.mb-6.w-full
      [:label.block.text-sm.font-medium.text-gray-700.mb-1
       "Additional comments (optional)"]
      [:textarea.w-full.border.border-gray-300.rounded-md.p-2.text-sm.text-gray-700.focus:ring-1.focus:ring-blue-500.focus:border-blue-500
       {:rows 3
        :maxlength 500
        :placeholder "Tell us more..."
        :value (or comment-text "")
        :on {:input [[:store/assoc-in [::comment-text] :event/target.value]]}}]]
     [:div.flex.gap-3.justify-end.w-full
      [:button {:class ["px-4" "py-2" "text-sm" "font-medium" "rounded-full"
                        "border" "border-gray-300" "text-gray-700"
                        "bg-white" "hover:bg-gray-50"]
                :on {:click [[:ui/modal-close]]}}
       "Skip"]
      [:button {:class ["px-4" "py-2" "text-sm" "font-medium" "rounded-full"
                        "text-white" "bg-blue-600" "hover:bg-blue-700"
                        "disabled:opacity-50"]
                :disabled (not selected-reason)
                :on {:click [[:data/command
                              {:command/kind :command/subscription-exit-reason
                               :command/data {:exit-reason selected-reason
                                              :exit-reason-position (reason-position state)
                                              :exit-comment comment-text}}]
                             [:ui/modal-close]]}}
       "Submit"]]]))

(def modal
  {:modal-id :modals/subscription-exit-modal
   :on-load (fn [_state]
              [[:store/assoc-in [::selected-reason] nil]
               [:store/assoc-in [::comment-text] nil]
               [:store/assoc-in [::order] (shuffled-reason-ids)]])
   :render #'render})
